import React from 'react';
import { Link } from 'react-router-dom';
import {
  FiArrowRight,
  FiBarChart2,
  FiCheckCircle,
  FiLayers,
  FiMessageCircle,
  FiShield,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import type { IconType } from 'react-icons';

interface Feature {
  title: string;
  description: string;
  icon: IconType;
}

interface Module {
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  role: string;
}

interface Highlight {
  text: string;
  icon: IconType;
}

interface Stat {
  value: string;
  label: string;
}

interface FooterLink {
  label: string;
  to?: string;
  href?: string;
}

interface FooterSection {
  title: string;
  links: FooterLink[];
}

interface NavLinkItem {
  label: string;
  href: string;
}

const features: Feature[] = [
  {
    title: 'Visibilidade ponta a ponta',
    description:
      'Monitore os lotes desde a aquisição até a expedição de produtos em uma linha do tempo única.',
    icon: FiTrendingUp,
  },
  {
    title: 'Decisões guiadas por dados',
    description:
      'KPIs em tempo real conectam produção, estoque e qualidade para antecipar gargalos e reduzir perdas.',
    icon: FiBarChart2,
  },
  {
    title: 'Governança e compliance',
    description:
      'Rastreabilidade completa das certificações e auditorias com alertas automáticos para garantir conformidade.',
    icon: FiShield,
  },
  {
    title: 'Onboarding rápido',
    description:
      'Fluxos pré-configurados, integrações com ERPs e suporte consultivo aceleram a adoção em semanas, não meses.',
    icon: FiCheckCircle,
  },
];

const modules: Module[] = [
  {
    title: 'Aquisição inteligente',
    description:
      'Gestão de fornecedores, certificações e lotes integrada às inspeções de qualidade de entrada.',
  },
  {
    title: 'Produção conectada',
    description:
      'Controle das ordens, apontamentos, consumo de madeira e produtividade das máquinas em um único cockpit.',
  },
  {
    title: 'Qualidade total',
    description: 'Auditorias digitais, análise de defeitos e workflows de retrabalho para eliminar reincidências.',
  },
  {
    title: 'Estoque e expedição',
    description: 'Inventário de produtos em tempo real, planejamento de cargas e rastreabilidade até o cliente final.',
  },
  {
    title: 'Manutenção preditiva',
    description: 'Histórico completo das máquinas e ordens de manutenção com alertas de disponibilidade.',
  },
  {
    title: 'Pessoas e indicadores',
    description: 'Desenvolvimento de operadores, treinamentos e dashboards executivos para acompanhar resultados.',
  },
];

const testimonials: Testimonial[] = [
  {
    quote: 'A Iapotech Wood trouxe clareza para toda a cadeia. Reduzimos em 32% o estoque parado de madeira.',
    author: 'Patrícia Braga',
    role: 'Diretora Industrial',
  },
  {
    quote:
      'Conseguimos conectar chão de fábrica, qualidade e expedição em uma única fonte de verdade para o time.',
    author: 'Eduardo Lima',
    role: 'COO',
  },
];

const priorities: Highlight[] = [
  {
    icon: FiTrendingUp,
    text: 'Maximizar yield dos lotes e ocupação das linhas.',
  },
  {
    icon: FiLayers,
    text: 'Sincronizar compras, produção e expedição com o mesmo plano.',
  },
  {
    icon: FiUsers,
    text: 'Empoderar operadores com checklists e alertas móveis.',
  },
];

const heroStats: Stat[] = [
  { value: '35%', label: 'redução média em perdas operacionais' },
  { value: '+18', label: 'módulos especializados para madeira' },
  { value: '99,9%', label: 'disponibilidade em nuvem' },
];

const trustBenchmarks = ['Weyerhaeuser', 'West Fraser', 'Stora Enso'];

const footerSections: FooterSection[] = [
  {
    title: 'Área Administrativa',
    links: [
      { label: 'Entrar no painel', to: '/login' },
      { label: 'Documentação interna', href: 'https://iapotech.com/admin/docs' },
      { label: 'Central de suporte', href: 'mailto:suporte@iapotech.com' },
    ],
  },
  {
    title: 'Soluções',
    links: [
      { label: 'Aquisição inteligente', to: '/login' },
      { label: 'Produção conectada', to: '/login' },
      { label: 'Qualidade & Compliance', to: '/login' },
    ],
  },
  {
    title: 'Recursos',
    links: [
      { label: 'Diagnóstico gratuito', href: '#contato' },
      { label: 'Guia de integrações', href: 'https://iapotech.com/guia-integracoes' },
      { label: 'Blog de operações', href: 'https://iapotech.com/blog' },
    ],
  },
];

const navLinks: NavLinkItem[] = [
  { label: 'Soluções', href: '#solucoes' },
  { label: 'Módulos', href: '#modulos' },
  { label: 'Resultados', href: '#resultados' },
  { label: 'Contato', href: '#contato' },
];

export const LandingPage: React.FC = () => {
  return (
    <div className="landing">
      <header className="landing-hero text-light">
        <nav className="landing-topbar">
          <div className="container d-flex flex-wrap align-items-center justify-content-between gap-3">
            <Link to="/" className="landing-topbar__brand">
              <img
                src="/TraceWood.png"
                alt="TraceWood"
                className="landing-topbar__logo"
              />
            </Link>
            <div className="landing-topbar__links">
              {navLinks.map((navItem) => (
                <a key={navItem.label} href={navItem.href}>
                  {navItem.label}
                </a>
              ))}
            </div>
            <div className="landing-topbar__actions">
              <a href="#contato" className="landing-topbar__contact">
                Diagnóstico gratuito
              </a>
              <Link to="/login" className="landing-button landing-button--ghost">
                Área Administrativa
              </Link>
            </div>
          </div>
        </nav>
        <div className="container py-5">
          <div className="row align-items-center g-5">
            <div className="col-12 col-lg-7">
              <span className="landing-pill landing-pill--contrast">Plataforma Industrial Iapotech Wood</span>
              <h1>Transforme a cadeia de madeira em uma operação inteligente, sustentável e lucrativa.</h1>
              <p>
                O hub digital que conecta aquisição, produção, qualidade, estoque e pessoas em um fluxo contínuo e
                mobile-first com KPIs acionáveis.
              </p>
              <div className="landing-hero__note">
                Benchmarks globais como Weyerhaeuser, West Fraser e Stora Enso inspiraram a paleta verde-floresta e os
                acentos amadeirados, reforçando sustentabilidade e confiança.
              </div>
              <div className="landing-hero__actions d-flex flex-column flex-sm-row gap-3">
                <Link to="/login" className="landing-button landing-button--primary">
                  Entrar na plataforma
                  <FiArrowRight />
                </Link>
                <a href="#contato" className="landing-button landing-button--ghost">
                  Falar com especialista
                </a>
              </div>
              <div className="row row-cols-2 row-cols-sm-3 g-3 landing-hero__stats">
                {heroStats.map(({ value, label }) => (
                  <div key={value} className="col">
                    <div className="landing-stat">
                      <strong>{value}</strong>
                      <span>{label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-12 col-lg-5">
              <div className="landing-panel">
                <p className="landing-panel__eyebrow">Prioridades do setor</p>
                <h3>Roadmap inteligente para operações madeireiras.</h3>
                <ul className="landing-panel__list">
                  {priorities.map(({ icon: Icon, text }) => (
                    <li key={text}>
                      <Icon aria-hidden="true" />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
                <div className="landing-trust">
                  <small>Benchmarks analisados</small>
                  <div className="landing-trust__logos">
                    {trustBenchmarks.map((name) => (
                      <span key={name}>{name}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="landing-section" id="solucoes">
          <div className="container">
            <div className="landing-section__header text-center">
              <span className="landing-pill landing-pill--muted">Por que Iapotech Wood?</span>
              <h2>Poder estratégico para quem lidera operações de madeira.</h2>
              <p>
                Integre áreas críticas com UX consistente: rotinas automatizadas, visibilidade em tempo real e decisões
                orientadas por dados no desktop ou no celular.
              </p>
            </div>
            <div className="row g-4">
              {features.map((feature) => (
                <div key={feature.title} className="col-12 col-md-6 col-lg-3">
                  <article className="landing-card h-100">
                    <div className="landing-card__icon">
                      <feature.icon size={28} aria-hidden="true" />
                    </div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-section landing-section--alt" id="modulos">
          <div className="container">
            <div className="landing-section__header text-center text-lg-start">
              <span className="landing-pill landing-pill--muted">Módulos especializados</span>
              <h2>Todo o ciclo de vida sob controle, do campo ao cliente.</h2>
              <p>
                Inspiração direta das stacks das líderes globais para oferecer fluxos visuais claros e com hierarquia
                mobile-first.
              </p>
            </div>
            <div className="row g-4">
              {modules.map((module) => (
                <div key={module.title} className="col-12 col-md-6">
                  <article className="landing-card landing-card--module h-100 d-flex flex-column flex-sm-row gap-3">
                    <div className="landing-card__icon landing-card__icon--muted">
                      <FiLayers size={24} aria-hidden="true" />
                    </div>
                    <div>
                      <h3>{module.title}</h3>
                      <p>{module.description}</p>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-section landing-section--testimonials" id="resultados">
          <div className="container">
            <div className="landing-section__header text-center">
              <FiMessageCircle size={32} className="landing-icon-accent" aria-hidden="true" />
              <span className="landing-pill landing-pill--muted">Resultados reais</span>
              <h2>Executivos que já aceleram com a Iapotech Wood.</h2>
            </div>
            <div className="row g-4">
              {testimonials.map((testimonial) => (
                <div key={testimonial.author} className="col-12 col-md-6">
                  <figure className="landing-card landing-card--testimonial h-100">
                    <blockquote>{testimonial.quote}</blockquote>
                    <figcaption>
                      <strong>{testimonial.author}</strong>
                      <span>{testimonial.role}</span>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="landing-section" id="contato">
          <div className="container">
            <div className="row g-4 align-items-center">
              <div className="col-12 col-lg-7">
                <span className="landing-pill landing-pill--muted">Pronto para começar?</span>
                <h2>Solicite uma demonstração guiada com o time de especialistas.</h2>
                <p>
                  Estruturamos um plano de rollout que respeita o ritmo da sua operação e garante ganhos rápidos em cada
                  etapa.
                </p>
                <ul className="landing-cta__list">
                  <li>
                    <FiUsers aria-hidden="true" />
                    Diagnóstico colaborativo com líderes de cada área.
                  </li>
                  <li>
                    <FiLayers aria-hidden="true" />
                    Blueprint de integrações e migração de dados.
                  </li>
                  <li>
                    <FiCheckCircle aria-hidden="true" />
                    Acompanhamento semanal até a captura total de valor.
                  </li>
                </ul>
              </div>
              <div className="col-12 col-lg-5">
                <div className="landing-cta__card h-100">
                  <h3>Agende uma conversa</h3>
                  <p>Disponível em português e inglês.</p>
                  <a href="mailto:contato@iapotech.com" className="landing-button landing-button--primary w-100">
                    contato@iapotech.com
                    <FiArrowRight />
                  </a>
                  <a href="tel:+551199999999" className="landing-button landing-button--outline w-100">
                    +55 11 99999-9999
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="container">
          <div className="row gy-4">
            <div className="col-12 col-lg-4">
              <div className="landing-footer__brand">
                <span className="landing-footer__logo">Iapotech Wood</span>
                <p>
                  Plataforma integrada para operações madeireiras modernas. Conecte aquisição, produção, qualidade e
                  logística com governança digital ponta a ponta.
                </p>
                <div className="landing-footer__badge">Operação 24/7 • Cloud First</div>
              </div>
            </div>
            {footerSections.map((section) => (
              <div key={section.title} className="col-6 col-md-4 col-lg-2">
                <h6 className="landing-footer__title">{section.title}</h6>
                <ul className="landing-footer__links">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      {link.to ? (
                        <Link to={link.to}>{link.label}</Link>
                      ) : (
                        <a href={link.href}>{link.label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="landing-footer__bottom d-flex flex-column flex-md-row justify-content-between align-items-center">
            <p className="mb-0">
              &copy; 2025 Iap\u00f3tech. Todos os direitos reservados.
            </p>
            <div className="landing-footer__bottom-links">
              <a href="mailto:contato@iapotech.com">contato@iapotech.com</a>
              <a href="https://iapotech.com/politica-privacidade">Política de Privacidade</a>
              <a href="https://iapotech.com/seguranca">Status &amp; Segurança</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};


